<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import PdfDateRangeModal from './PdfDateRangeModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ConversationAPI from 'dashboard/api/inbox/conversation';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const [showPdfDateRangeModal, togglePdfDateRangeModal] = useToggle(false);
const pdfDownloadMode = ref('single');

const currentChat = computed(() => store.getters.getSelectedChat);
const currentUser = computed(() => store.getters.getCurrentUser);
const isAdministrator = computed(
  () => currentUser.value?.role === 'administrator'
);

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const filenameDateSuffix = ({ startDate, endDate } = {}) => {
  if (!startDate && !endDate) return '';

  return `-${startDate || 'start'}-${endDate || 'end'}`;
};

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  items.push({
    icon: 'i-lucide-file-down',
    label: t('CONTACT_PANEL.DOWNLOAD_CONVERSATION_PDF'),
    action: 'download_pdf',
    value: 'download_pdf',
  });

  if (isAdministrator.value) {
    items.push({
      icon: 'i-lucide-folder-down',
      label: t('CONTACT_PANEL.DOWNLOAD_ALL_CONVERSATIONS'),
      action: 'download_all_pdfs',
      value: 'download_all_pdfs',
    });
  }

  return items;
});

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'download_pdf') {
    pdfDownloadMode.value = 'single';
    togglePdfDateRangeModal(true);
  } else if (action === 'download_all_pdfs') {
    pdfDownloadMode.value = 'all';
    togglePdfDateRangeModal(true);
  }
};

const downloadPdf = async dateRange => {
  try {
    if (pdfDownloadMode.value === 'single') {
      const response = await ConversationAPI.downloadPDF(
        currentChat.value.id,
        dateRange
      );
      downloadBlob(
        response.data,
        `conversation-${currentChat.value.id}${filenameDateSuffix(dateRange)}.pdf`
      );
    } else {
      const response = await ConversationAPI.downloadAllPDFs(dateRange);
      downloadBlob(
        response.data,
        `conversations${filenameDateSuffix(dateRange)}.zip`
      );
    }

    togglePdfDateRangeModal(false);
  } catch (error) {
    useAlert(t('CONTACT_PANEL.DOWNLOAD_ERROR'));
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <PdfDateRangeModal
      v-if="showPdfDateRangeModal"
      :show="showPdfDateRangeModal"
      :mode="pdfDownloadMode"
      @cancel="togglePdfDateRangeModal(false)"
      @download="downloadPdf"
    />
  </div>
</template>
